import type { MetricKey } from '../analytics.js';
import type { Actionable } from './actions.js';
import type { AgentCardKind } from './index.js';
import type { OpCardRoute } from './routes.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A · ANALİZ KARTLARI — alan ajanı: analiz (Faz 8 dalga 2, 30 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bu dosyanın sahibi ANALİZ ajanı. Kurallar (`./index.ts` başlığında tam tarif):
 *
 *   · Yeni tür = `AnalysisAgentCard` birliğine dal + `ANALYSIS_CARD_KINDS`e
 *     ad + web `tool-renderers/analysis.tsx`te görünüm (eksikse DERLEME kırılır).
 *   · Hangi araç/işlem bu kartı çizer: `ANALYSIS_TOOL_CARDS` (araç adı) ve
 *     `ANALYSIS_OP_CARDS` (katalog operasyonu deseni — API üreticisiyle aynı,
 *     `apps/api/src/agent/cards/analysis/index.ts`).
 *   · Düğme: kartın `actions` alanı (`Actionable<…>`, `./actions.ts`) — sunucu
 *     yazar, sunucu yürütür; model değil.
 *   · 🔴 KİŞİSEL VERİ YOK — analiz kartları toplu sayı taşır; satır adı
 *     kampanya/set/reklam/ürün adıdır (kişi değil).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * SAYI KURALI — "araçtan gelmeyen sayı söyleme"
 * ───────────────────────────────────────────────────────────────────────────
 * Karttaki her sayı SUNUCUDA hesaplandı: kırılım payı, katkı ayrıştırması
 * (CPM×CTR×CVR), tahmin eğrisi, harmanlanmış ROAS. Ekran yalnız BİÇİMLER
 * (`formatMetric`, `METRIC_DEFINITIONS`) ve yönü renklendirir; bölme/çarpma
 * yazmaz. Sayılar ANA birimde, para birimi kartta (karışıksa `null` +
 * `mixedCurrency` → para bağlı metrik "—").
 *
 * Metin DEĞİL anahtar: bulgu/öneri/dayanak cümleleri `code` + `params`
 * olarak gelir (`dashboard.agentChat.cards.<tür>.*`), ekran çevirir.
 * Platformun KENDİ metni (Google öneri türü açıklaması, Meta öneri başlığı)
 * `title`/`detail` alanında düz metin — markdown/HTML değil.
 */

type Range = { from: string; to: string };

/** Kırılım boyutu — platformlar arası ORTAK ad (eşleme API'de, `get_breakdown`). */
export const BREAKDOWN_DIMENSIONS = [
  'age',
  'gender',
  'age_gender',
  'placement',
  'device',
  'region',
  'country',
  'hour',
  'platform',
  'account',
  'campaign',
  'status',
  /* LinkedIn üye demografisi (B2B) — yalnız o platformun okumasında. */
  'seniority',
  'job_function',
  'company_size',
] as const;
export type BreakdownDimension = (typeof BREAKDOWN_DIMENSIONS)[number];

/** Birleşik okumada bir platformun durumu (kart "şu platform katılmadı, çünkü…" der). */
export type AnalysisSourceStatus = 'ok' | 'empty' | 'unsupported' | 'not_connected' | 'failed';

/**
 * `metrics` kartının (tipi `core.ts`te) ANALİZ uzantısı — hepsi İSTEĞE BAĞLI:
 * eski sohbet satırlarındaki kartlar bu alanlar olmadan da geçerli kalmalı.
 */
export interface MetricsCardExtras {
  /** Zaman serisi — gün başına seçili metrikler (grafik). */
  series?: { date: string; values: Partial<Record<MetricKey, number | null>> }[];
  /** Grafikte çizilecek metrikler (en çok 2 — iki eksen). Yoksa ekran `series`ten seçer. */
  seriesMetrics?: MetricKey[];
  /**
   * GRUPLU seri — platform/hesap/kampanya başına TEK metrik (`groupMetric`),
   * çizgi başına bir grup (`analytics_timeseries groupBy`). Günler ORTAK
   * (`groupDates`), değerler aynı sırada — tarih her grupta tekrar etmesin
   * (365 gün × 6 grup kartın 64 KB tavanını aşardı).
   */
  seriesGroups?: {
    key: string;
    label: string;
    platform: string | null;
    values: (number | null)[];
  }[];
  groupDates?: string[];
  groupMetric?: MetricKey;
  /** Platform kırılımı — platform başına aynı metrikler (bant). */
  platforms?: {
    platform: string;
    /** Platformun hesabının para birimi — kartınkinden farklıysa burada. */
    currency?: string | null;
    items: { key: MetricKey; value: number | null }[];
  }[];
  /** Kapsam: filtreli okuma (`analytics_summary`) — hangi platformlar/varlıklar. */
  scope?: { platforms: string[]; entities: number | null; level: 'account' | 'campaign' | 'adset' | 'ad' | null } | null;
}

/** Tek bir A/B deneyi (Meta study, Google experiment, LinkedIn/TikTok testi). */
export interface ExperimentItem {
  id: string;
  name: string | null;
  /** Platformun durumu, normalize: taslak/yürüyor/bitti/iptal/bilinmiyor. */
  status: 'draft' | 'scheduled' | 'running' | 'ended' | 'canceled' | 'unknown';
  /** Platformun tür adı (`SPLIT_TEST`, `SEARCH_CUSTOM`…) — ham kod. */
  type: string | null;
  start: string | null;
  end: string | null;
  /** Kazananı belirleyen metrik — platform söylemiyorsa `null`. */
  metric: MetricKey | null;
  arms: {
    id: string;
    name: string | null;
    control: boolean;
    /** Trafik payı 0–1. */
    traffic: number | null;
    values: Partial<Record<MetricKey, number | null>>;
  }[];
  /** Kazanan kolun `id`si — platform ya da SUNUCU (aynı metrikte, anlamlılık yoksa `null`). */
  winner: string | null;
  /** Güven 0–1 (platformdan). */
  confidence: number | null;
  /** Kolların sonucu okunabildi mi (`false` → kart yalnız yapıyı gösterir). */
  hasResults: boolean;
}

export type AnalysisAgentCard =
  /**
   * KIRILIM — yaş / cinsiyet / yerleşim / cihaz / bölge / saat (+ platform,
   * hesap, kampanya, durum — `analytics_breakdown`).
   * `rows[].key` ORTAK değer (`18-24`, `female`, `mobile`, `feed`, `00`…);
   * `label` sunucunun okunur adı ya da `null` (ekran anahtardan çevirir).
   */
  | Actionable<{
      kind: 'breakdown';
      dimension: BreakdownDimension;
      /** Tek platform okumasında platform; birleşik okumada `null`. */
      platform: string | null;
      range: Range;
      currency: string | null;
      /** Sıralama/vurgu metriği. */
      metric: MetricKey;
      /** Sütunlar. */
      metrics: MetricKey[];
      rows: {
        key: string;
        label: string | null;
        values: Partial<Record<MetricKey, number | null>>;
        /** Vurgu metriğinde toplam içindeki pay (0–1). */
        share: number | null;
        /** Bu satıra katkı veren platformlar (birleşik okuma). */
        platforms?: string[];
      }[];
      total: number;
      state: 'ok' | 'empty' | 'partial';
      /** Birleşik okumada platform başına durum — `unsupported` = boyut o platformda yok. */
      sources?: { platform: string; status: AnalysisSourceStatus; code?: string | null }[];
      /** Platformların para birimi farklı → para metrikleri toplanmadı ("—"). */
      mixedCurrency?: boolean;
    }>
  /**
   * SIRALAMA — kampanya / set / reklam. Satır eylemleri (durdur / başlat /
   * bütçe ±%) kartın `actions`ında, `scope` = satırın `id`si.
   */
  | Actionable<{
      kind: 'ranking';
      level: 'campaign' | 'adset' | 'ad';
      metric: MetricKey;
      direction: 'desc' | 'asc';
      range: Range;
      currency: string | null;
      rows: {
        id: string;
        platform: string;
        name: string;
        status: string | null;
        /** Platform referansı (işlem parametresi) — yoksa `null`. */
        ref: string | null;
        value: number | null;
        values: Partial<Record<MetricKey, number | null>>;
        /**
         * Etkin günlük bütçe. CBO'da kampanyanınki; ABO'da (kampanya bütçesiz)
         * açık set'lerin TOPLAMI — o zaman `budgetLevel: 'adset'` ve bütçe
         * düğmesi YOK (kampanyada bütçe yok; set başına değişmeli).
         */
        dailyBudget: number | null;
        budgetLevel?: 'campaign' | 'adset' | null;
        /** Satırın kendi para birimi (karışık hesaplarda). */
        currency?: string | null;
        group: 'best' | 'worst' | null;
      }[];
      total: number;
    }>
  /**
   * "NEDEN?" BULGUSU — kanıt serisi + önerilen eylemler (eylemler `actions`).
   * `diagnose_change` KPI değişimini çarpanlarına SUNUCUDA ayırır
   * (`decomposition`); takip kopması şüphesi `tracking`te.
   */
  | Actionable<{
      kind: 'insight';
      range: Range;
      currency: string | null;
      /** Sorunun anahtarı (`cards.insight.question.<code>`) — yoksa `null`. */
      question: string | null;
      /** Soru parametreleri (metrik, kapsam adı). */
      questionParams?: Record<string, string | number>;
      findings: {
        id: string;
        severity: 'info' | 'warn' | 'critical';
        /** `cards.insight.finding.<code>` */
        code: string;
        params?: Record<string, string | number>;
        metric: MetricKey | null;
        evidence: { date: string; value: number | null }[];
        target: { level: 'account' | 'campaign' | 'adset' | 'ad'; ref: string | null; name: string | null } | null;
      }[];
      /**
       * KATKI AYRIŞTIRMASI — KPI = çarpanların çarpımı (ör. dönüşüm = harcama ÷
       * CPM × CTR × CVR). `share` log-katkının payı (−1…1; işaret yön):
       * `ln(sonra/önce)` toplamı KPI'nın `ln` değişimine eşit.
       */
      decomposition?: {
        metric: MetricKey;
        /** `cards.insight.formula.<code>` — ekrandaki formül satırı. */
        formula: string;
        previous: Range;
        before: number | null;
        after: number | null;
        deltaPct: number | null;
        factors: {
          key: 'spend' | 'cpm' | 'ctr' | 'cvr' | 'aov' | 'impressions' | 'clicks' | 'conversions' | 'cpc';
          /** KPI'yı bu çarpan büyütünce KPI da büyür mü (`inverse` → CPM gibi paydada). */
          inverse: boolean;
          before: number | null;
          after: number | null;
          deltaPct: number | null;
          share: number | null;
        }[];
      } | null;
      /** Takip kopması şüphesi — dönüşüm düştü ama trafik düşmedi vb. */
      tracking?: { suspected: boolean; signals: { code: string; params?: Record<string, string | number> }[] } | null;
    }>
  /**
   * TAHMİN — bütçe → sonuç eğrisi (Meta erişim tahmini, Google teklif/bütçe
   * simülasyonu, Reach Planner, TikTok/LinkedIn tahmini). "Bu bütçeyi uygula"
   * eylemi `actions`ta (`input: 'amount'` + `bind`, eğri üzerinden seçilen tutar).
   */
  | Actionable<{
      kind: 'forecast';
      platform: string;
      source:
        | 'meta_reach'
        | 'google_budget_sim'
        | 'google_bid_sim'
        | 'google_reach_planner'
        | 'tiktok_estimate'
        | 'linkedin_forecast'
        | 'history'
        | 'other';
      currency: string | null;
      period: 'daily' | 'lifetime';
      /** X ekseni — bütçe (varsayılan) ya da teklif (`google_bid_sim`). */
      axis?: 'budget' | 'bid';
      /** Eğrinin Y ekseni — `reach`, `conversions`, `clicks`, `impressions`… */
      resultKey: string;
      points: {
        budget: number;
        result: number | null;
        low: number | null;
        high: number | null;
      }[];
      current: { budget: number | null; result: number | null } | null;
      recommended: { budget: number; result: number | null } | null;
      target: { level: 'account' | 'campaign' | 'adset'; ref: string | null; name: string | null } | null;
      /** Kaynak notu (`cards.forecast.note.<code>`) — ör. geçmişten tahmin, güven aralığı yok. */
      note?: string | null;
    }>
  /** BÜTÇE DAĞILIMI — kanal/kampanya bugün vs öneri. */
  | Actionable<{
      kind: 'budget_split';
      currency: string | null;
      period: 'daily' | 'lifetime';
      rows: {
        id: string;
        platform: string;
        level: 'platform' | 'campaign' | 'adset';
        name: string;
        ref: string | null;
        current: number | null;
        proposed: number | null;
        /** Sıralamanın metriği ve değeri (gerekçe: "ROAS 3,1 → üst dilim"). */
        metric?: number | null;
        role?: 'winner' | 'loser' | null;
      }[];
      totalCurrent: number | null;
      totalProposed: number | null;
      /** Önerinin dayanağı (`cards.budget_split.basis.<code>`). */
      basis: string | null;
      basisParams?: Record<string, string | number>;
      range?: Range | null;
    }>
  /**
   * MAĞAZA SATIŞI — ciro/sipariş/en çok satan + reklamla birleşik ROAS.
   * 🔴 Sipariş MÜŞTERİSİ yok: yalnız toplamlar ve ürün satırları.
   */
  | Actionable<{
      kind: 'sales';
      range: Range;
      currency: string | null;
      store: { platform: string; name: string | null } | null;
      totals: {
        revenue: number | null;
        orders: number | null;
        aov: number | null;
        adSpend: number | null;
        /** Mağaza cirosu / reklam harcaması (MER) — gerçek ROAS DEĞİL, ekran böyle etiketler. */
        blendedRoas: number | null;
      };
      series: { date: string; revenue: number | null; orders: number | null; spend: number | null }[];
      topProducts: {
        productId: string;
        title: string;
        imageUrl: string | null;
        units: number | null;
        revenue: number | null;
      }[];
      /** Mağaza ve reklam para birimi farklı → birleşik ROAS hesaplanmadı. */
      mixedCurrency?: boolean;
    }>
  /**
   * KARŞILAŞTIRMA — iki varlık (aynı dönem) ya da iki dönem (aynı hesap).
   * `deltaPct` sağın sola göre değişimi; farklı para biriminde para metrikleri
   * karşılaştırılmaz (`comparable: false`).
   */
  | Actionable<{
      kind: 'compare';
      mode: 'entities' | 'periods';
      currency: string | null;
      comparable: boolean;
      left: CompareSide;
      right: CompareSide;
      items: { key: MetricKey; left: number | null; right: number | null; deltaPct: number | null }[];
    }>
  /**
   * PLATFORM ÖNERİLERİ — Google/Meta/X'in kendi önerileri. Satır eylemi
   * uygula / yoksay (`scope` = önerinin `id`si); uygulanamayan platformda
   * (X — uygulama işlemi yok) düğme yok.
   */
  | Actionable<{
      kind: 'recommendations';
      platform: string;
      items: {
        /** Kart içi kimlik (`r0`, `r1`…) — satır eyleminin `scope`u. Platformun öneri
         *  kimliği yalnız eylemin sabit parametresinde (Meta imzası 2000 karakter). */
        id: string;
        /** Platformun tür kodu (`CAMPAIGN_BUDGET`, `SCALE_GOOD_CAMPAIGN`…). */
        type: string;
        /** Platformun kendi başlığı/açıklaması — DÜZ METİN. */
        title: string | null;
        detail: string | null;
        /** Platformun öngördüğü etki (önce → sonra). */
        impact: { key: string; before: number | null; after: number | null }[];
        target: { level: 'account' | 'campaign' | 'adset' | 'ad' | 'keyword'; ref: string | null; name: string | null } | null;
        dismissed: boolean;
      }[];
      total: number;
    }>
  /** A/B TESTİ — kollar, sonuç, kazanan. */
  | Actionable<{
      kind: 'experiment';
      platform: string;
      source: 'meta_study' | 'google_experiment' | 'linkedin_experiment' | 'tiktok_split_test' | 'other';
      items: ExperimentItem[];
      total: number;
    }>;

/** Karşılaştırma kartının bir tarafı. */
export interface CompareSide {
  /** Varlık adı ya da dönem (`2026-09-01…2026-09-07`). */
  label: string;
  level: 'account' | 'campaign' | 'adset' | 'ad';
  id: string | null;
  platform: string | null;
  range: Range;
  currency: string | null;
}

export const ANALYSIS_CARD_KINDS = [
  'breakdown',
  'ranking',
  'insight',
  'forecast',
  'budget_split',
  'sales',
  'compare',
  'recommendations',
  'experiment',
] as const;

/**
 * Araç adı → tür (ekran araçları, çekirdek araçlar).
 *
 * `analyze_campaigns` çekirdek listede `table` idi (Faz 8 öncesi); satır
 * eylemli `ranking`e geçti — birleşik sözlükte (`AGENT_TOOL_CARDS`) alan
 * satırı çekirdeği EZER (yayılım sırası).
 */
export const ANALYSIS_TOOL_CARDS: Readonly<Record<string, readonly AgentCardKind[]>> = {
  analyze_campaigns: ['ranking'],
  analytics_summary: ['metrics'],
  analytics_timeseries: ['metrics'],
  analytics_breakdown: ['breakdown'],
  analytics_ranking: ['ranking'],
  analytics_compare: ['compare'],
  get_breakdown: ['breakdown'],
  diagnose_change: ['insight'],
  forecast_budget: ['forecast'],
  suggest_budget_split: ['budget_split'],
  store_sales: ['sales'],
};

/**
 * Katalog okuma operasyonu deseni → tür. API üreticisiyle birebir
 * (`cards.spec.ts`). Bir işlem TEK türe gider (ilk eşleşen üretici).
 *
 * Tahmin (`meta.targeting.reach_estimate`, `tiktok.targeting.estimate_size`,
 * `x.audience.estimate`) KİTLE büyüklüğü — kampanya alanının `audience`
 * kartı; burada değil. `meta.reach_frequency.predict` bir YAZMA işlemi (onay
 * kartı yolu) — okuma üreticisi hiç çağrılmaz; Meta eğrisi `forecast_budget`ten.
 */
export const ANALYSIS_OP_CARDS: readonly OpCardRoute<AgentCardKind>[] = [
  { match: 'meta.insights.query', kind: 'breakdown' },
  { match: 'meta.insights.report_get', kind: 'breakdown' },
  { match: 'tiktok.report.run', kind: 'breakdown' },
  { match: 'linkedin.report.demographics', kind: 'breakdown' },
  { match: 'x.report.job_status', kind: 'breakdown' },
  { match: 'google.recommendation.list', kind: 'recommendations' },
  { match: 'meta.recommendation.list', kind: 'recommendations' },
  { match: 'x.recommendation.list', kind: 'recommendations' },
  { match: 'meta.study.list', kind: 'experiment' },
  { match: 'google.experiment.list', kind: 'experiment' },
  { match: 'linkedin.experiment.list', kind: 'experiment' },
  { match: 'linkedin.experiment.results', kind: 'experiment' },
  { match: 'google.bidding.list_simulations', kind: 'forecast' },
  { match: 'google.reach_plan.forecast', kind: 'forecast' },
  { match: 'tiktok.rf.estimate', kind: 'forecast' },
  { match: 'linkedin.media_plan.forecast', kind: 'forecast' },
];

/**
 * Canlı veri (`GET /agent/cards/:id/data`) şekilleri — analiz kartları kişisel
 * veri taşımıyor, sayılar kart üretildiği anın görüntüsü. Tazelik gereken bir
 * tür olursa buraya.
 */
export interface AnalysisCardLiveData {}
